import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShieldCheck } from "@phosphor-icons/react";
import api from "../lib/api";
import { isNative } from "../lib/native";

// Double authentification des administrateurs (audit sécurité C15).
// Visible uniquement pour un compte administrateur.
export default function AdminMfaSection() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [password, setPassword] = useState("");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/mfa/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, []);

  if (!status?.is_admin) return null;

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup", { password });
      setSetup(data);
      setPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("account.error"));
    } finally {
      setBusy(false);
    }
  };

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enable", { code: code.trim() });
      if (isNative() && data.token) {
        try { localStorage.setItem("hitmax_token", data.token); } catch {}
      }
      setBackupCodes(data.backup_codes || []);
      setSetup(null);
      setStatus((s) => ({ ...s, enabled: true }));
      toast.success(t("account.mfa_enabled"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("account.error"));
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full h-11 px-3.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold/60 transition-colors mb-3";
  const button = "w-full h-11 rounded-xl bg-gold text-black font-bold text-sm hover:bg-gold/90 transition-colors disabled:opacity-60";

  return (
    <div className="hm-card p-5 mt-4" data-testid="account-mfa-section">
      <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-white/60 mb-4">
        <ShieldCheck size={17} weight="bold" color="#D4AF37" /> {t("account.mfa_title")}
      </p>

      {backupCodes && (
        <div className="mb-4" data-testid="account-mfa-backup-codes">
          <p className="text-sm text-amber-300/90 mb-2">{t("account.mfa_backup_hint")}</p>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm">
            {backupCodes.map((c) => <span key={c} className="px-2 py-1 rounded bg-white/5 text-center">{c}</span>)}
          </div>
        </div>
      )}

      {status.enabled ? (
        <p className="text-sm text-emerald-300/90" data-testid="account-mfa-on">{t("account.mfa_on")}</p>
      ) : setup ? (
        <form onSubmit={enable}>
          <p className="text-sm text-white/60 mb-3">{t("account.mfa_scan")}</p>
          {setup.qr && <img src={setup.qr} alt={t("account.mfa_qr_alt")} className="w-44 h-44 mx-auto mb-3 rounded-lg bg-white p-2" />}
          <p className="text-[11px] text-white/40 break-all mb-3">{t("account.mfa_manual")} <span className="font-mono text-white/70">{setup.secret}</span></p>
          <input className={input} inputMode="numeric" autoComplete="one-time-code" placeholder={t("account.mfa_code")}
            aria-label={t("account.mfa_code")} value={code} onChange={(e) => setCode(e.target.value)} data-testid="account-mfa-code" />
          <button type="submit" disabled={busy || code.trim().length < 6} className={button} data-testid="account-mfa-enable-btn">
            {t("account.mfa_enable")}
          </button>
        </form>
      ) : (
        <form onSubmit={start}>
          <p className="text-sm text-white/60 mb-3">
            {status.enforced ? t("account.mfa_required_hint") : t("account.mfa_hint")}
          </p>
          <input className={input} type="password" autoComplete="current-password" placeholder={t("account.current_password")}
            aria-label={t("account.current_password")} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="account-mfa-password" />
          <button type="submit" disabled={busy || !password} className={button} data-testid="account-mfa-start-btn">
            {t("account.mfa_start")}
          </button>
        </form>
      )}
    </div>
  );
}
